import * as React from 'react'

import { isSameDay, startOfMonth } from 'date-fns'
import { Control, FieldPath, FieldValues, useController } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  ErrorText,
  Grid,
  Label,
  RadioButton,
  Select,
  SelectOptionInterface,
  Separator,
  Stack,
} from '@enechain/ecloud-designsystem'
import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import {
  EEXContractModel,
  FormNumberInputWithUnit,
} from '@enechain/eposition-ui'
import { getEndOfMonthDay } from '@enechain/eposition-utils'

import { useTransactionFormOption } from '~/pages/Simulation/hooks/useTransactionFormOption'

type Props<T extends FieldValues> = {
  schemaKeyName: FieldPath<T>
  control: Control<T>
  areaCode: AreaCode
  isPhysicalFuture: boolean
}

export const SimulationLegForm = <T extends FieldValues>({
  schemaKeyName,
  control,
  areaCode,
  isPhysicalFuture,
}: Props<T>): React.ReactElement => {
  const { t } = useTranslation(['domain', 'common'])

  const { field: deliveryPeriodTypeField } = useController({
    name: `${schemaKeyName}.deliveryPeriodType` as FieldPath<T>,
    control,
  })

  const {
    field: deliveryDateRangeField,
    fieldState: { error: deliveryDateRangeError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange` as FieldPath<T>,
    control,
  })

  const {
    fieldState: { error: deliveryStartDateError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange.deliveryStartDate` as FieldPath<T>,
    control,
  })

  const {
    fieldState: { error: deliveryEndDateError },
  } = useController({
    name: `${schemaKeyName}.deliveryDateRange.deliveryEndDate` as FieldPath<T>,
    control,
  })

  const { field: deliveryHourTypeField } = useController({
    name: `${schemaKeyName}.deliveryHourType` as FieldPath<T>,
    control,
  })

  const { field: eexProductCodeField } = useController({
    name: `${schemaKeyName}.eexProductCode` as FieldPath<T>,
    control,
  })

  const { field: eexInstrumentField } = useController({
    name: `${schemaKeyName}.eexInstrument` as FieldPath<T>,
    control,
  })

  const { deliveryPeriodTypes, eexContracts } = useTransactionFormOption({
    isSummary: false,
    selectedAreaCode: areaCode,
    selectedDeliveryPeriodType: deliveryPeriodTypeField.value,
    selectedDeliveryHourType: deliveryHourTypeField.value,
    isPhysicalFuture,
  })

  const deliveryDateRangeErrorMessages =
    deliveryDateRangeError?.message ??
    deliveryStartDateError?.message ??
    deliveryEndDateError?.message

  return (
    <Stack gap="4">
      <Grid alignItems="center" gridGap="4" gridTemplateColumns="1fr 4fr">
        <Label required>{t('domain:shared.delivery_period_type.title')}</Label>
        <RadioButton
          onValueChange={({ value }): void => {
            deliveryPeriodTypeField.onChange(Number(value))
          }}
          value={deliveryPeriodTypeField.value?.toString() ?? ''}
          radios={deliveryPeriodTypes}
        />
        <Label required>{t('domain:shared.delivery_date_range')}</Label>
        <Stack gap="1">
          <Select<EEXContractModel & SelectOptionInterface, false>
            isMulti={false}
            getOptionValue={(option) =>
              option.value.deliveryStartDate.toString()
            }
            onChange={(item): void => {
              if (item?.value === undefined) {
                deliveryDateRangeField.onChange({
                  deliveryStartDate: startOfMonth(new Date()),
                  deliveryEndDate: getEndOfMonthDay(new Date()),
                })
                return
              }
              deliveryDateRangeField.onChange({
                deliveryStartDate: item.value.deliveryStartDate,
                deliveryEndDate: getEndOfMonthDay(item.value.deliveryEndDate),
              })
              eexProductCodeField.onChange(item.value.code)
              eexInstrumentField.onChange(item.value.instrument)
            }}
            options={eexContracts}
            selectedOptions={
              eexContracts.find(
                (contract: EEXContractModel & SelectOptionInterface) =>
                  isSameDay(
                    contract.value.deliveryStartDate,
                    deliveryDateRangeField.value?.deliveryStartDate ??
                      new Date(),
                  ) &&
                  isSameDay(
                    contract.value.deliveryEndDate,
                    deliveryDateRangeField.value?.deliveryEndDate ?? new Date(),
                  ),
              ) ?? {
                label: t('common:form.label.unspecified'),
                value: {
                  deliveryStartDate: startOfMonth(new Date()),
                  deliveryEndDate: getEndOfMonthDay(new Date()),
                  code: '',
                  instrument: '',
                },
              }
            }
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
          {deliveryDateRangeErrorMessages !== undefined && (
            <ErrorText>{deliveryDateRangeErrorMessages}</ErrorText>
          )}
        </Stack>
      </Grid>
      <Separator />
      <Grid alignItems="center" gridGap="4" gridTemplateColumns="1fr 4fr">
        <Label required>
          {t('domain:model.simulation.column.transaction_unit_price')}
        </Label>
        <FormNumberInputWithUnit
          unit={t('common:unit.yen_per_kwh')}
          name={`${schemaKeyName}.unitPricePerKwh` as FieldPath<T>}
          control={control}
        />
      </Grid>
    </Stack>
  )
}
